import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * Interface para estatísticas da fila de processamento
 */
export interface QueueStats {
  pending: number;
  processing: number;
  completed24h: number;
  failed24h: number;
  successRate: number;
  avgProcessingTime: number;
}

/**
 * Interface representando um item da fila de processamento de flows
 */
export interface QueueItem {
  id: string;
  queue_type: string;
  status: string;
  attempts: number;
  max_attempts: number;
  created_at: string;
  processed_at: string | null;
  error_message: string | null;
  payload: any;
  provider_slug: string | null;
  tenant_id: string | null;
  tenant_name?: string;
  flow_name?: string;
  processing_time?: number;
  contact_phone?: string; // Telefone do contato para filtro de pesquisa
}

/**
 * Interface para alerta de saúde da fila
 */
export interface QueueAlert {
  id: string;
  type: 'warning' | 'error';
  message: string;
  count?: number;
}

/**
 * Interface para dados de volume temporal
 */
export interface QueueVolumeData {
  hour: string;
  completed: number;
  failed: number;
}

/**
 * Hook para buscar estatísticas da fila de processamento
 * Usa queries de agregação otimizadas para contagem precisa
 */
export function useQueueStats() {
  return useQuery({
    queryKey: ['queue-stats'],
    queryFn: async () => {
      const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      
      // Queries paralelas para melhor performance
      const [
        pendingResult,
        processingResult,
        completed24hResult,
        failed24hResult,
        completedItemsResult,
      ] = await Promise.all([
        // 1. Contagem de pending (total atual)
        supabase
          .from('flow_queue')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'pending'),
        
        // 2. Contagem de processing (total atual)
        supabase
          .from('flow_queue')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'processing'),
        
        // 3. Contagem de completed nas últimas 24h
        supabase
          .from('flow_queue')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'completed')
          .gte('created_at', twentyFourHoursAgo),
        
        // 4. Contagem de failed nas últimas 24h
        supabase
          .from('flow_queue')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'failed')
          .gte('created_at', twentyFourHoursAgo),
        
        // 5. Sample de itens completed para calcular tempo médio (apenas campos necessários)
        supabase
          .from('flow_queue')
          .select('created_at, processed_at')
          .eq('status', 'completed')
          .not('processed_at', 'is', null)
          .gte('created_at', twentyFourHoursAgo)
          .order('processed_at', { ascending: false })
          .limit(500),
      ]);

      // Extrair contagens dos resultados
      const pending = pendingResult.count || 0;
      const processing = processingResult.count || 0;
      const completed24h = completed24hResult.count || 0;
      const failed24h = failed24hResult.count || 0;
      
      const total24h = completed24h + failed24h;
      const successRate = total24h > 0 ? (completed24h / total24h) * 100 : 100;

      // Calcular tempo médio de processamento (em segundos)
      const completedItems = completedItemsResult.data || [];
      let avgProcessingTime = 0;
      if (completedItems.length > 0) {
        const totalTime = completedItems.reduce((sum, item) => {
          const created = new Date(item.created_at).getTime();
          const processed = new Date(item.processed_at!).getTime();
          return sum + (processed - created);
        }, 0);
        avgProcessingTime = totalTime / completedItems.length / 1000; // Converter para segundos
      }

      const stats: QueueStats = {
        pending,
        processing,
        completed24h,
        failed24h,
        successRate,
        avgProcessingTime,
      };

      return stats;
    },
    refetchInterval: 30000, // Auto-refresh a cada 30 segundos
  });
}

/**
 * Hook para buscar itens recentes da fila com informações enriquecidas
 * Otimizado com batch queries para evitar N+1 problem
 */
export function useQueueItems(
  statusFilter: string = 'all', 
  providerFilter: string = 'all',
  limit: number = 50
) {
  return useQuery({
    queryKey: ['queue-items', statusFilter, providerFilter, limit],
    queryFn: async () => {
      // ============ FASE 1: Buscar itens brutos ============
      let query = supabase
        .from('flow_queue')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      if (providerFilter !== 'all') {
        query = query.eq('provider_slug', providerFilter);
      }

      const { data: rawItems, error } = await query;
      if (error) throw error;
      if (!rawItems || rawItems.length === 0) return [];

      // ============ FASE 2: Extrair IDs únicos de todos os itens ============
      const tenantIds = new Set<string>();
      const flowIds = new Set<string>();
      const executionIds = new Set<string>();
      
      // Mapear item -> IDs para enriquecimento posterior
      const itemExecutionMap = new Map<string, string>(); // itemId -> executionId
      const itemFlowIdFromPayload = new Map<string, string>(); // itemId -> flowId (do payload)
      const itemContactPhoneFromPayload = new Map<string, string>(); // itemId -> phone (do payload)

      rawItems.forEach(item => {
        // Tenant ID
        const payload = item.payload as any;
        const tenantId = item.tenant_id || payload?.payload?.tenant_id;
        if (tenantId) tenantIds.add(tenantId);
        
        // Flow ID direto do payload
        const flowId = payload?.payload?.flow_id;
        if (flowId) {
          flowIds.add(flowId);
          itemFlowIdFromPayload.set(item.id, flowId);
        }
        
        // Execution ID (para buscar flow_id e contact_id)
        const executionId = payload?.payload?.execution_id;
        if (executionId) {
          executionIds.add(executionId);
          itemExecutionMap.set(item.id, executionId);
        }
        
        // Telefone direto do payload (start_flow)
        const contactPhone = payload?.payload?.initial_variables?.contact_phone;
        if (contactPhone) {
          itemContactPhoneFromPayload.set(item.id, contactPhone);
        }
      });

      // ============ FASE 3: Fazer queries em batch (paralelo) ============
      const [tenantsResult, flowsResult, executionsResult] = await Promise.all([
        // Query 1: Buscar todos os tenants de uma vez
        tenantIds.size > 0 
          ? supabase.from('tenants').select('id, name').in('id', Array.from(tenantIds))
          : Promise.resolve({ data: [] }),
        
        // Query 2: Buscar todos os flows de uma vez
        flowIds.size > 0
          ? supabase.from('flows').select('id, name').in('id', Array.from(flowIds))
          : Promise.resolve({ data: [] }),
        
        // Query 3: Buscar todas as executions de uma vez (com contact_id e flow_id)
        executionIds.size > 0
          ? supabase.from('flow_executions').select('id, flow_id, contact_id').in('id', Array.from(executionIds))
          : Promise.resolve({ data: [] }),
      ]);

      // Extrair contact_ids e flow_ids adicionais das executions
      const contactIds = new Set<string>();
      const additionalFlowIds = new Set<string>();
      const executionMap = new Map<string, { flow_id: string | null; contact_id: string | null }>();
      
      (executionsResult.data || []).forEach((exec: any) => {
        executionMap.set(exec.id, { flow_id: exec.flow_id, contact_id: exec.contact_id });
        if (exec.contact_id) contactIds.add(exec.contact_id);
        if (exec.flow_id && !flowIds.has(exec.flow_id)) additionalFlowIds.add(exec.flow_id);
      });

      // Query 4 e 5: Buscar contatos e flows adicionais em paralelo
      const [contactsResult, additionalFlowsResult] = await Promise.all([
        contactIds.size > 0
          ? supabase.from('contacts').select('id, phone').in('id', Array.from(contactIds))
          : Promise.resolve({ data: [] }),
        
        additionalFlowIds.size > 0
          ? supabase.from('flows').select('id, name').in('id', Array.from(additionalFlowIds))
          : Promise.resolve({ data: [] }),
      ]);

      // ============ FASE 4: Criar Maps para lookup O(1) ============
      const tenantMap = new Map<string, string>(
        (tenantsResult.data || []).map((t: any) => [t.id, t.name])
      );
      
      // Combinar flows do payload e flows das executions
      const flowMap = new Map<string, string>(
        [...(flowsResult.data || []), ...(additionalFlowsResult.data || [])]
          .map((f: any) => [f.id, f.name])
      );
      
      const contactMap = new Map<string, string>(
        (contactsResult.data || []).map((c: any) => [c.id, c.phone || ''])
      );

      // ============ FASE 5: Enriquecer itens com lookup direto (sem queries) ============
      const enrichedData: QueueItem[] = rawItems.map(item => {
        const payload = item.payload as any;
        const tenantId = item.tenant_id || payload?.payload?.tenant_id;
        
        // Tenant name via lookup
        const tenant_name = tenantId ? (tenantMap.get(tenantId) || 'N/A') : 'N/A';
        
        // Flow name via lookup (prioriza payload, depois execution)
        let flowId = itemFlowIdFromPayload.get(item.id);
        const executionId = itemExecutionMap.get(item.id);
        
        if (!flowId && executionId) {
          const execution = executionMap.get(executionId);
          if (execution?.flow_id) flowId = execution.flow_id;
        }
        const flow_name = flowId ? (flowMap.get(flowId) || 'N/A') : 'N/A';
        
        // Contact phone via lookup (prioriza payload, depois execution->contact)
        let contact_phone = itemContactPhoneFromPayload.get(item.id) || '';
        
        if (!contact_phone && executionId) {
          const execution = executionMap.get(executionId);
          if (execution?.contact_id) {
            contact_phone = contactMap.get(execution.contact_id) || '';
          }
        }
        
        // Calcular tempo de processamento
        let processing_time = 0;
        if (item.processed_at) {
          const created = new Date(item.created_at).getTime();
          const processed = new Date(item.processed_at).getTime();
          processing_time = (processed - created) / 1000; // em segundos
        }

        return {
          ...item,
          tenant_name,
          flow_name,
          processing_time,
          contact_phone,
        };
      });

      return enrichedData;
    },
    refetchInterval: 30000, // Auto-refresh a cada 30 segundos
  });
}

/**
 * Hook para buscar volume de processamento por hora
 * Otimizado para buscar apenas campos necessários com limite adequado
 * @param hours - Número de horas para buscar
 */
export function useQueueVolume(hours: number = 24) {
  return useQuery({
    queryKey: ['queue-volume', hours],
    queryFn: async () => {
      const since = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
      
      // Buscar apenas campos necessários com limite adequado para alta carga
      const { data, error } = await supabase
        .from('flow_queue')
        .select('processed_at, status')
        .in('status', ['completed', 'failed'])
        .gte('created_at', since)
        .not('processed_at', 'is', null)
        .order('processed_at', { ascending: false })
        .limit(10000); // Limite maior para cenários de alta carga

      if (error) throw error;

      // Agrupar por hora
      const hourlyData = new Map<string, QueueVolumeData>();
      
      // Inicializar todas as horas
      for (let i = 0; i < hours; i++) {
        const hour = new Date(Date.now() - i * 60 * 60 * 1000);
        const hourKey = hour.toISOString().slice(0, 13) + ':00';
        hourlyData.set(hourKey, { hour: hourKey, completed: 0, failed: 0 });
      }
      
      // Contabilizar itens por hora
      data?.forEach((item) => {
        if (item.processed_at) {
          const hourKey = item.processed_at.slice(0, 13) + ':00';
          const hourData = hourlyData.get(hourKey);
          if (hourData) {
            if (item.status === 'completed') {
              hourData.completed++;
            } else if (item.status === 'failed') {
              hourData.failed++;
            }
          }
        }
      });

      // Converter para array ordenado por hora
      return Array.from(hourlyData.values())
        .sort((a, b) => a.hour.localeCompare(b.hour));
    },
    refetchInterval: 60000, // Atualizar a cada minuto
  });
}

/**
 * Hook para buscar alertas de saúde da fila
 */
/**
 * Hook para buscar conversas com steps duplicados (mesmo nó executado 2+ vezes)
 * Indica problemas de concorrência no processamento
 */
export function useDuplicatedConversations() {
  return useQuery({
    queryKey: ['duplicated-conversations'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_duplicated_step_conversations');
      
      if (error) {
        console.error('Erro ao buscar duplicações:', error);
        return new Map<string, number>();
      }
      
      // Retorna Map de conversation_id -> max_duplicates para lookup rápido
      return new Map(
        (data || []).map((d: { conversation_id: string; max_duplicates: number }) => 
          [d.conversation_id, d.max_duplicates]
        )
      );
    },
    refetchInterval: 30000, // Auto-refresh a cada 30 segundos
  });
}

/**
 * Hook para gerar alertas de saúde da fila
 * Usa queries de agregação otimizadas para contagens precisas
 */
export function useQueueAlerts() {
  return useQuery({
    queryKey: ['queue-alerts'],
    queryFn: async () => {
      const alerts: QueueAlert[] = [];
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
      const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      
      // Queries paralelas para melhor performance
      const [
        oldPendingResult,
        completed24hResult,
        failed24hResult,
        avgTimeResult,
      ] = await Promise.all([
        // 1. Itens pendentes antigos (mais de 5 minutos)
        supabase
          .from('flow_queue')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'pending')
          .lt('created_at', fiveMinutesAgo),
        
        // 2. Contagem de completed nas últimas 24h
        supabase
          .from('flow_queue')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'completed')
          .gte('created_at', twentyFourHoursAgo),
        
        // 3. Contagem de failed nas últimas 24h
        supabase
          .from('flow_queue')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'failed')
          .gte('created_at', twentyFourHoursAgo),
        
        // 4. Sample para calcular tempo médio de processamento
        supabase
          .from('flow_queue')
          .select('created_at, processed_at')
          .eq('status', 'completed')
          .not('processed_at', 'is', null)
          .gte('created_at', twentyFourHoursAgo)
          .order('processed_at', { ascending: false })
          .limit(500),
      ]);

      // Verificar itens pendentes antigos
      const oldPendingCount = oldPendingResult.count || 0;
      if (oldPendingCount > 0) {
        alerts.push({
          id: 'old-pending',
          type: 'warning',
          message: `${oldPendingCount} itens pendentes há mais de 5 minutos`,
          count: oldPendingCount,
        });
      }

      // Verificar taxa de falha
      const completed24h = completed24hResult.count || 0;
      const failed24h = failed24hResult.count || 0;
      const total24h = completed24h + failed24h;
      
      if (total24h > 0) {
        const failureRate = (failed24h / total24h) * 100;
        if (failureRate > 10) {
          alerts.push({
            id: 'high-failure-rate',
            type: 'error',
            message: `Taxa de falha alta: ${failureRate.toFixed(1)}% nas últimas 24h`,
            count: failed24h,
          });
        }
      }

      // Verificar tempo médio de processamento
      const completedItems = avgTimeResult.data || [];
      if (completedItems.length > 0) {
        const totalTime = completedItems.reduce((sum, item) => {
          const created = new Date(item.created_at).getTime();
          const processed = new Date(item.processed_at!).getTime();
          return sum + (processed - created);
        }, 0);
        const avgTime = totalTime / completedItems.length / 1000; // em segundos
        
        if (avgTime > 60) { // Mais de 1 minuto em média
          alerts.push({
            id: 'high-avg-time',
            type: 'warning',
            message: `Tempo médio de processamento alto: ${avgTime.toFixed(0)}s`,
          });
        }
      }

      return alerts;
    },
    refetchInterval: 30000, // Atualizar a cada 30 segundos
  });
}
